import { useMemo, useState } from "react";
import { BuildingOffice2Icon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

import { InstitutionRecord, ProviderRecord } from "#/api/provider.type";
import { useSearchProviders } from "#/hooks/useSearchProviders";
import Card from "#/components/shared/card";
import Input from "#/components/shared/input";
import ThemedSpinner from "#/components/shared/spinner";
import ThemedModal from "#/components/shared/ThemedModal";
import { SubmitButton } from "#/components/shared/buttons/submit-button";
import Typography from "#/components/shared/typography";

interface StartRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MIN_QUERY_LENGTH = 2;

const getDisplayName = (entry: ProviderRecord | InstitutionRecord) => {
  if ((entry as ProviderRecord).first_name || (entry as ProviderRecord).last_name) {
    const provider = entry as ProviderRecord;
    const fullName = `${provider.first_name ?? ""} ${provider.last_name ?? ""}`.trim();
    return provider.name || fullName || "Provider";
  }
  return entry.name || "Institution";
};

const getLocation = (entry: ProviderRecord | InstitutionRecord) =>
  [entry.city, entry.state].filter(Boolean).join(", ");

export function StartRequestModal({ isOpen, onClose }: StartRequestModalProps) {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const trimmedQuery = query.trim();
  const { data, isFetching } = useSearchProviders(trimmedQuery);

  const providerResults = data?.list ?? [];
  const institutionResults = data?.institutions?.list ?? [];
  const showResults = trimmedQuery.length >= MIN_QUERY_LENGTH;
  const nothingFound =
    showResults && !isFetching && providerResults.length === 0 && institutionResults.length === 0;

  const handleSelect = (slug?: string | null) => {
    if (!slug) return;
    navigate(`/request-records/${encodeURIComponent(slug)}`);
    setQuery("");
    onClose();
  };

  const instruction = useMemo(() => {
    if (!showResults) {
      return "Search for a clinic, hospital, or provider to request records from.";
    }
    if (isFetching) {
      return "Searching Harper's directory…";
    }
    if (nothingFound) {
      return "We couldn’t find a match. Try a different name or location.";
    }
    return "Select the provider or institution that should receive your request.";
  }, [showResults, isFetching, nothingFound]);

  return (
    <ThemedModal
      isOpen={isOpen}
      onClose={() => {
        setQuery("");
        onClose();
      }}
      title="Start a new medical records request"
      description="Choose the provider or facility that should receive the authorization."
      icon={<BuildingOffice2Icon className="h-6 w-6" />}
    >
      <div className="space-y-5">
        <Input
          label="Find a provider"
          placeholder="Type at least 2 characters"
          leftIcon={<MagnifyingGlassIcon className="h-4 w-4 text-terracotta" />}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          fullWidth
        />
        <Typography variant="body2" className="text-brown-600">
          {instruction}
        </Typography>

        {isFetching && showResults ? (
          <div className="flex justify-center py-6">
            <ThemedSpinner />
          </div>
        ) : null}

        {nothingFound ? (
          <Card variant="cream" elevation={1}>
            <Card.Body>
              <Typography variant="body2" className="text-brown-700">
                No providers or institutions matched “{trimmedQuery}”.
              </Typography>
            </Card.Body>
          </Card>
        ) : null}

        {showResults && !isFetching && !nothingFound ? (
          <div className="space-y-6 max-h-[360px] overflow-y-auto pr-1">
            {providerResults.length > 0 ? (
              <div className="space-y-2">
                <Typography variant="subtitle2" className="text-terracotta">
                  Providers
                </Typography>
                <ul className="divide-y divide-cream-200 rounded-xl border border-cream-200">
                  {providerResults.map((provider) => (
                    <li key={provider.slug ?? provider.id} className="p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <Typography variant="subtitle1" className="text-brown-900">
                            {getDisplayName(provider)}
                          </Typography>
                          {provider.medical_specialty?.name && (
                            <Typography variant="body2" className="text-brown-600">
                              {provider.medical_specialty.name}
                            </Typography>
                          )}
                          {getLocation(provider) && (
                            <Typography variant="caption" className="text-brown-500">
                              {getLocation(provider)}
                            </Typography>
                          )}
                        </div>
                        <SubmitButton
                          size="sm"
                          hideArrow
                          color="brown"
                          isDisabled={!provider.slug}
                          onClick={() => handleSelect(provider.slug)}
                        >
                          Use provider
                        </SubmitButton>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {institutionResults.length > 0 ? (
              <div className="space-y-2">
                <Typography variant="subtitle2" className="text-terracotta">
                  Institutions
                </Typography>
                <ul className="divide-y divide-cream-200 rounded-xl border border-cream-200">
                  {institutionResults.map((institution) => (
                    <li key={institution.slug ?? institution.id} className="p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <Typography variant="subtitle1" className="text-brown-900">
                            {getDisplayName(institution)}
                          </Typography>
                          {getLocation(institution) && (
                            <Typography variant="caption" className="text-brown-500">
                              {getLocation(institution)}
                            </Typography>
                          )}
                        </div>
                        <SubmitButton
                          size="sm"
                          hideArrow
                          color="brown"
                          isDisabled={!institution.slug}
                          onClick={() => handleSelect(institution.slug)}
                        >
                          Use institution
                        </SubmitButton>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </ThemedModal>
  );
}

export default StartRequestModal;
